import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, MoreVertical, Pencil, RotateCcw, Stethoscope } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SortableTableHead } from "../components/SortableTableHead";
import { inputClass, selectClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import { auditedInvoiceStatusFilterOptions } from "../data";
import { useAdminInvoices } from "../hooks/useAdminInvoices";
import { InvoiceNatureBadge } from "../components/InvoiceNatureBadge";
import type { Invoice } from "../types";
import { cn } from "@/shared/lib/cn";

type AuditedInvoice = Invoice;

type InvoiceActionsMenuProps = {
  isOpen: boolean;
  labels: { view: string; edit: string };
  onToggle: () => void;
  onClose: () => void;
  onView: () => void;
  onEdit: () => void;
};

function InvoiceActionsMenu({
  isOpen,
  labels,
  onToggle,
  onClose,
  onView,
  onEdit,
}: InvoiceActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
      >
        <MoreVertical size={14} />
      </button>

      {isOpen && (
        <div className="card-shadow absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-xl border border-border bg-card p-1.5">
          <button
            type="button"
            onClick={() => {
              onView();
              onClose();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            <Eye size={14} className="text-primary" />
            {labels.view}
          </button>
          <button
            type="button"
            onClick={() => {
              onEdit();
              onClose();
            }}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground transition-colors hover:bg-card-muted"
          >
            <Pencil size={14} className="text-accent" />
            {labels.edit}
          </button>
        </div>
      )}
    </div>
  );
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

function getAuditedStatus(invoice: AuditedInvoice) {
  return invoice.status === "rejected" ? "rejected" : "approved";
}

export function AuditedInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.audited;
  const { exportTable } = usePageActions();
  const navigate = useNavigate();
  const { invoices: auditedInvoicesList, loading, error } = useAdminInvoices({
    mode: "audited",
  });

  const [topSearch, setTopSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const { sortKey, sortDirection, toggleSort } = useTableSort("displayId", "desc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setStatusFilter("");
    setTableSearch("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return auditedInvoicesList.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.reference} ${invoice.fileName}`.toLowerCase();
      const auditedStatus = getAuditedStatus(invoice);

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byStatus = statusFilter ? auditedStatus === statusFilter : true;

      return byTopSearch && byTableSearch && byStatus;
    });
  }, [auditedInvoicesList, topSearch, statusFilter, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (invoice, key) => {
        if (key === "displayId") return invoice.displayId;
        if (key === "hospital") return invoice.hospital;
        if (key === "company") return invoice.company;
        if (key === "invoice") return invoice.fileName;
        if (key === "status") return getAuditedStatus(invoice);
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const columnDefs = [
    { key: "displayId", label: labels.columns.id, sortable: true },
    { key: "hospital", label: labels.columns.hospital, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "nature", label: labels.columns.nature, sortable: true },
    { key: "invoice", label: labels.columns.invoice, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  const handleDownload = (invoice: AuditedInvoice) => {
    exportTable(
      `facture-${invoice.displayId}.csv`,
      [labels.columns.id, labels.columns.hospital, labels.columns.company, labels.columns.status],
      [
        [
          String(invoice.displayId),
          invoice.hospital,
          invoice.company,
          getAuditedStatus(invoice) === "approved"
            ? labels.statusApproved
            : labels.statusRejected,
        ],
      ],
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref={getDashboardHomeHref()}
        items={[
          { label: t.safecheck.nav.invoices, href: "/dashboard/invoices" },
          { label: labels.title },
        ]}
      />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
        {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
      </div>

      <div className="theme-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={topSearch}
              onChange={(event) => {
                setTopSearch(event.target.value);
                setPage(1);
              }}
              placeholder={labels.searchPlaceholder}
              className={`${inputClass} w-full min-w-[200px] max-w-xs`}
            />
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className={`${selectClass} w-full min-w-[180px] max-w-xs`}
            >
              {auditedInvoiceStatusFilterOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.allStatuses}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
            aria-label={t.safecheck.reset}
          >
            <RotateCcw size={15} />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[960px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((invoice, index) => {
                const auditedStatus = getAuditedStatus(invoice);
                return (
                  <tr
                    key={invoice.id}
                    className={cn(
                      "border-b border-border last:border-0 hover:bg-card-muted/30",
                      index % 2 === 1 ? "bg-card-muted/20" : "bg-card",
                    )}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      <Link
                        to={`/dashboard/invoices/${invoice.displayId}`}
                        className="text-foreground transition-colors hover:text-primary"
                      >
                        {invoice.displayId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.company}</td>
                    <td className="px-4 py-3">
                      <InvoiceNatureBadge
                        nature={invoice.invoiceNature}
                        isForfaitMensuel={invoice.isForfaitMensuel}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleDownload(invoice)}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {labels.download}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                          auditedStatus === "approved"
                            ? "bg-accent/15 text-accent"
                            : "bg-danger-soft text-danger",
                        )}
                      >
                        {auditedStatus === "approved"
                          ? labels.statusApproved
                          : labels.statusRejected}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to="/dashboard/acts"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                        >
                          <Stethoscope size={14} />
                          {labels.actsDiagnostics}
                        </Link>
                        <InvoiceActionsMenu
                          isOpen={openMenuId === invoice.id}
                          labels={labels.actions}
                          onToggle={() =>
                            setOpenMenuId((current) =>
                              current === invoice.id ? null : invoice.id,
                            )
                          }
                          onClose={() => setOpenMenuId(null)}
                          onView={() => navigate(`/dashboard/invoices/${invoice.displayId}`)}
                          onEdit={() => navigate("/dashboard/invoices/submit")}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing}{" "}
            {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of}{" "}
            {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setPage(pageNum)}
                className={cn(
                  "min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                  pageNum === currentPage
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-muted-foreground hover:bg-card-muted",
                )}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
